// src/components/KyomuRosterImport.jsx
// 教務システムの「受講者一覧」の取り込み【2026-09-18】
//
//   いきなり入れない。2段階。
//     1. ファイルを選ぶ → サーバーが読んで「何がどこへ入るか」を返す（まだ保存しない）
//     2. 授業ごとに行き先を決めて「取り込む」
//
//   決められること:
//     - 履修時間割番号ごとに、新しく作る / 既存の授業に入れる / 取り込まない
//     - 子コース（主開講が別）を親にまとめるか、別々に作るか
//     - 履修取消の学生を入れるか除くか
//   既存授業の候補には「どこから来た授業か」（manaba由来・手作業）を添える。
//   manaba で作った同名の授業と二重にならないようにするため。
import React, { useEffect, useRef, useState } from 'react';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

/**
 * @param {function} onDone           取り込んだあとに親へ知らせる
 * @param {File}     incomingFile     画面全体に落とされたファイル（親から渡る）
 * @param {function} onIncomingTaken  受け取ったら親に空にしてもらう
 */
/**
 * @param {boolean} embedded  true なら自前のファイル選択を出さない。
 *   名簿の入口は1つにまとめてあり、そこから渡ってくる。
 */
export default function KyomuRosterImport({
  onDone, incomingFile, onIncomingTaken, onNotKyomu, embedded,
}) {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [choice, setChoice]   = useState({});   // key → { to, parent, termId }
  const [excludeCancelled, setExcludeCancelled] = useState(true);
  // すでに履修中の人が今回「履修取消」なら名簿から外すか【2026-10-01】標準は外す（出席記録は残る）
  const [removeCancelled, setRemoveCancelled] = useState(true);
  // 曜日時限から自動で出席を開始するか【2026-09-28】標準はオフ。
  //   取り込んだ授業の曜日時限をまとめてオン／オフにする。個別に外すのは授業スケジュールの詳細設定で
  const [autoAttendance, setAutoAttendance] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');
  const [result, setResult]   = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setChoice({});
    setMsg(''); setErr(''); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ── 1段目: 読むだけ ──
  const doPreview = async (f) => {
    setBusy(true); setErr(''); setMsg(''); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch('/api/csv-import/kyomu/preview', { method: 'POST', headers: auth(), body: fd });
      const d = await r.json();
      // 受講者一覧ではなかった。manaba の名簿なら親がそちらへ回す【2026-09-18】
      if (!d.success && d.code === 'LOOKS_LIKE_MANABA' && onNotKyomu) {
        setFile(null);
        onNotKyomu(f);
        return;
      }
      if (!d.success) throw new Error(d.message || '読み取れませんでした');
      setPreview(d.preview);
      const init = {};
      d.preview.groups.forEach(g => {
        init[g.key] = { to: g.suggested, parent: g.suggestedParent, termId: g.termId || '' };
      });
      setChoice(init);
      setMsg(`${d.preview.groups.length}件の授業を読み取りました。行き先を確認してください。`);
    } catch (e) { setErr(e.message); setPreview(null); }
    finally { setBusy(false); }
  };

  const pick = (f) => { setFile(f); if (f) doPreview(f); };

  // 画面のどこかに落とされたファイルを受け取る。受け取ったら親に知らせて空にしてもらう
  useEffect(() => {
    if (!incomingFile) return;
    pick(incomingFile);
    if (onIncomingTaken) onIncomingTaken();
  }, [incomingFile]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key, patch) => setChoice(c => ({ ...c, [key]: { ...c[key], ...patch } }));

  // 行き先に既存の授業を選んだら、その授業に何が足されるかをサーバーに聞く【2026-10-05】
  //   （履修者が何人増えるか・曜日時限が増えるか。manaba などで入れた分と重ならないかを見る）
  const [diffs, setDiffs] = useState({});
  const targetsKey = JSON.stringify(Object.fromEntries(
    Object.entries(choice).filter(([, v]) => v && /^\d+$/.test(String(v.to || ''))).map(([k, v]) => [k, Number(v.to)])));
  useEffect(() => {
    if (!file || !preview || targetsKey === '{}') { setDiffs({}); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('targets', targetsKey);
        const r = await fetch('/api/csv-import/kyomu/preview', { method: 'POST', headers: auth(), body: fd });
        const d = await r.json();
        if (!alive || !d.success) return;
        const m = {};
        d.preview.groups.forEach(g => { if (g.diff) m[g.key] = g.diff; });
        setDiffs(m);
      } catch (e) { /* 見えなくても取り込みはできる */ }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [targetsKey, file, preview]);

  // ── 2段目: 実際に入れる ──
  const doImport = async () => {
    if (!file) return;
    const going = preview.groups.filter(g => (choice[g.key] || {}).to !== 'skip');
    if (going.length === 0) { setErr('取り込む授業がありません'); return; }
    const students = going.reduce((n, g) => n + (excludeCancelled ? g.counts.active : g.counts.total), 0);
    // 学籍番号が同じなのに名前が違う学生がいる授業は、サーバーが取り込まない【2026-10-05】
    const blocked = going.filter(g => g.nameMismatchCount > 0 && !(choice[g.key] || {}).allowNameMismatch);
    if (!window.confirm(`授業 ${going.length}件・履修者 ${students}人を取り込みます。`
      + (blocked.length ? `\n※ 名前の違う学生がいる ${blocked.length}件（${blocked.map(g => g.courseName).join('・')}）は取り込みません。` : '')
      + `\n曜日時限からの自動出席: ${autoAttendance ? 'オン（時刻になると自動で受付を始めます）' : 'オフ'}\n\nよろしいですか？`)) return;

    setBusy(true); setErr(''); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('classMap', JSON.stringify(choice));
      fd.append('excludeCancelled', excludeCancelled ? '1' : '0');
      fd.append('removeCancelled', excludeCancelled && removeCancelled ? '1' : '0');
      fd.append('autoAttendance', autoAttendance ? '1' : '0');
      const r = await fetch('/api/csv-import/kyomu', { method: 'POST', headers: auth(), body: fd });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '取り込めませんでした');
      setResult(d.result);
      setMsg(d.message);
      setPreview(null);
      if (onDone) onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // 名簿の入口は1つにまとめてある。ここは中身が受講者一覧だったときだけ出る。
  if (embedded && !busy && !preview && !result && !err) return null;

  return (
    <div className="card" data-kyomu-import>
      <div className="card-header"><h2>受講者一覧として取り込む（教務システム）</h2></div>

      {!embedded && (
        <>
          <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
            教務システムが出す「受講者一覧」をそのまま読みます（.xls / .xlsx / .csv）。
            <strong>選んだだけでは保存しません。</strong>
          </p>
          <input ref={inputRef} type="file" accept=".xls,.xlsx,.ods,.csv,.tsv"
                 disabled={busy}
                 onChange={e => pick(e.target.files[0] || null)} />
        </>
      )}

      {busy && <p style={{ color: '#6b7280' }}>処理中…</p>}
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {msg && <p style={{ color: '#166534' }}>{msg}</p>}

      {/* ── 取り込んだあとの結果 ── */}
      {result && <ImportResult result={result} onReset={reset} embedded={embedded} />}

      {/* ── 確認画面 ── */}
      {preview && (
        <>
          <div className="dash-strip" style={{ cursor: 'default', marginTop: '0.9rem' }}>
            <span className="dash-strip-icon">✓</span>
            <span className="dash-strip-body">
              <span className="dash-strip-t">
                {preview.academicYear}年度 ・ 授業 {preview.groups.length}件 ・ {preview.rowCount}行
              </span>
              <span className="dash-strip-m">
                {preview.fileName}（見出しは{preview.headerRow}行目）
                {preview.skipped > 0 && ` ・ 読み飛ばし ${preview.skipped}行`}
              </span>
            </span>
          </div>

          {/* 書式が変わっても読めるよう、見出しの言い換えや中身から列を探す【2026-09-28】
              見出しがそのまま一致しなかった列と、見つからなかった列だけ知らせる */}
          {preview.columns && (() => {
            const LABEL = { externalCode: '履修時間割番号', courseName: '授業名', courseCode: '科目コード',
              parentCode: '主開講時間割番号', parentName: '主開講授業名', dayPeriod: '開講曜講時',
              studentId: '学籍番号', name: '氏名', nameKana: '氏名カナ', academicYear: '年度',
              teacherName: '教員名', teacherCode: '教員コード', room: '教室（授業の教室へ）',
              cancelReason: '取消理由', cancelDate: '取消年月日', affiliation: '所属', gradeYear: '年次' };
            const guessed = Object.entries(preview.columns).filter(([, v]) => v.how !== 'exact');
            if (guessed.length === 0) return null;
            return (
              <p style={{ color: '#1e40af', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                ℹ 見出しが違う列を読み替えました：
                {guessed.map(([k, v]) => `「${v.header}」→${LABEL[k] || k}${v.how === 'values' ? '（中身から判断）' : ''}`).join('、')}。
                違っていたら取り込まずに教えてください。
              </p>
            );
          })()}
          {(preview.warnings || []).length > 0 && (
            <p style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.5rem 0' }}>
              ⚠ {preview.warnings.join('。')}。その項目は無しで取り込みます。
            </p>
          )}

          {preview.unknownPeriods.length > 0 && (
            <p style={{ color: '#b45309', fontSize: '0.85rem' }}>
              ⚠ {preview.unknownPeriods.join('・')}限の時刻が講時マスタにありません。
              このままだと曜日時限が作られません。教務管理の「講時の時刻」で設定してください。
            </p>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.8rem 0' }}>
            <input type="checkbox" checked={excludeCancelled}
                   onChange={e => setExcludeCancelled(e.target.checked)} />
            履修取消の学生を入れない
          </label>
          {excludeCancelled && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', margin: '-0.4rem 0 0.8rem 1.4rem' }}>
              <input type="checkbox" checked={removeCancelled} style={{ marginTop: '0.2rem' }}
                     onChange={e => setRemoveCancelled(e.target.checked)} />
              <span>
                すでに名簿にいる学生が履修取消になっていたら、名簿から外す
                <span style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280' }}>
                  出席記録は消えません。外した学生は、あとでこの取り込みを取り消しても名簿に戻りません（学生管理から戻せます）
                </span>
              </span>
            </label>
          )}

          {/* 自動出席【2026-09-28】曜日時限が読めた授業だけに効く。標準はオフ */}
          {preview.groups.some(g => g.schedules && g.schedules.length > 0) && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', margin: '0 0 0.8rem' }}>
              <input type="checkbox" checked={autoAttendance} style={{ marginTop: '0.2rem' }}
                     onChange={e => setAutoAttendance(e.target.checked)} />
              <span>
                曜日時限の時刻になったら、自動で出席の受付を始める
                <span style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280' }}>
                  取り込む授業の曜日時限をまとめてオンにします。特定の曜日時限だけ止めたいときは、
                  あとで授業スケジュールの「詳細設定」から外せます。
                </span>
              </span>
            </label>
          )}

          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {preview.groups.map(g => (
              <GroupRow key={g.key} g={g} preview={preview} diff={diffs[g.key]}
                        value={choice[g.key] || {}} onChange={patch => set(g.key, patch)} />
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.2rem', flexWrap: 'wrap' }}>
            <button className="btn-primary" disabled={busy} onClick={doImport}
                    style={{ minHeight: '3rem', minWidth: '12rem' }}>
              {busy ? '取り込み中…' : '取り込む'}
            </button>
            <button className="btn-secondary" disabled={busy} onClick={reset}>やめる</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── 授業1つぶんの行 ──────────────────────────────────────────────
/** 名前の比べ方（サーバーと同じ）：全角半角・空白・かっこの中・記号を無視【2026-10-05】 */
function nameKey(name) {
  return String(name || '').normalize('NFKC').toLowerCase()
    .replace(/[([{【〔「『][^)\]}】〕」』]*[)\]}】〕」』]/g, '')
    .replace(/[\s・･\-‐－―_／/:：,，.。、]/g, '');
}
/** 名前の近さ。同じ=1000、どちらかがもう一方を含む=500+長さ、それ以外は共通する一番長い部分の長さ */
function nameScore(a, b) {
  const x = nameKey(a), y = nameKey(b);
  if (!x || !y) return 0;
  if (x === y) return 1000;
  if (x.includes(y) || y.includes(x)) return 500 + Math.min(x.length, y.length);
  let best = 0;
  for (let i = 0; i < x.length; i++) {
    for (let j = i + best + 1; j <= x.length; j++) {
      if (y.includes(x.slice(i, j))) best = j - i; else break;
    }
  }
  return best;
}

function GroupRow({ g, value, onChange, preview, diff }) {
  const [open, setOpen] = useState(false);
  const skip = value.to === 'skip';

  return (
    <div style={{
      border: '1px solid ' + (skip ? '#e5e7eb' : '#dbe4f0'),
      borderLeft: '4px solid ' + (skip ? '#cbd5e1' : g.isChild ? '#d97706' : '#2563eb'),
      borderRadius: 10, padding: '0.8rem 1rem',
      background: skip ? '#f8fafc' : '#fff',
      opacity: skip ? 0.7 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1rem' }}>{g.courseName}</strong>
        {g.externalCode && (
          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
            {g.externalCode}
          </span>
        )}
        {g.isChild && (
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309',
                         background: '#fffbeb', border: '1px solid #fde68a',
                         borderRadius: 5, padding: '0.05rem 0.4rem' }}>
            子コース
          </span>
        )}
      </div>

      <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.2rem' }}>
        {g.termName || g.termText || '学期不明'}
        {g.schedules.length > 0 && ' ・ ' + g.schedules.map(s =>
          `${s.dayName}${s.period}限` + (s.known ? `（${s.startTime}〜${s.endTime}）` : '（時刻未設定）')
        ).join('・')}
        {g.room && ' ・ ' + g.room}
        {' ・ 履修 '}<strong>{g.counts.active}</strong>人
        {g.counts.cancelled > 0 && `（取消 ${g.counts.cancelled}人）`}
      </div>

      {/* 行き先 */}
      <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.82rem', color: '#4b5563' }}>行き先</label>
        <select value={value.to || 'new'} onChange={e => onChange({ to: e.target.value })}>
          <option value="new">新しく作る</option>
          {g.candidates.map(c => (
            <option key={c.id} value={String(c.id)}>
              {c.name}（{c.origin}・履修{c.enrolled}人{c.mine ? '' : '／他の先生'}
              {c.matchedBy === 'code' ? '・番号一致' : c.matchedBy === 'similar' ? '・名前が似ている' : '・名前一致'}）
            </option>
          ))}
          {/* 候補に出ない授業にも入れられるように、担当の先生のほかの授業も出す【2026-10-05】
              名前が近い順に並べ、一部が同じ（3文字以上）ものは「名前の一部が同じ」として上にまとめる */}
          {(() => {
            const others = ((preview && preview.ownerClasses) || [])
              .filter(c => !g.candidates.some(x => String(x.id) === String(c.id)))
              .map(c => ({ ...c, score: nameScore(g.courseName, c.name) }))
              .sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name), 'ja'));
            const near = others.filter(c => c.score >= 3);
            const rest = others.filter(c => c.score < 3);
            const opt = (c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}（{c.termName ? c.termName + '・' : ''}履修{c.enrolled}人）
              </option>
            );
            return (
              <>
                {near.length > 0 && <optgroup label="名前の一部が同じ授業">{near.map(opt)}</optgroup>}
                {rest.length > 0 && <optgroup label={`ほかの授業（${preview.academicYear}年度）`}>{rest.map(opt)}</optgroup>}
              </>
            );
          })()}
          <option value="skip">取り込まない</option>
        </select>

        {g.isChild && !skip && (
          <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <input type="checkbox" checked={!!value.parent}
                   onChange={e => onChange({ parent: e.target.checked })} />
            「{g.parentName}」にまとめる
            {!g.parentInFile && (
              <span style={{ color: '#b45309' }}>（親がこのファイルにありません）</span>
            )}
          </label>
        )}
      </div>

      {/* 学籍番号が同じなのに名前が違う学生【2026-10-05】
          いれば、この授業はまるごと取り込まない（名簿の打ち間違い・別の名簿の取り違えで別人に履修が付かないように） */}
      {g.nameMismatchCount > 0 && !skip && (
        <div style={{ marginTop: '0.45rem', padding: '0.5rem 0.7rem', borderRadius: 6,
                      background: value.allowNameMismatch ? '#fffbeb' : '#fef2f2',
                      border: '1px solid ' + (value.allowNameMismatch ? '#fcd34d' : '#fca5a5'), fontSize: '0.8rem' }}>
          <strong style={{ color: value.allowNameMismatch ? '#92400e' : '#b91c1c' }}>
            ⚠ 学籍番号が同じなのに、登録されている名前と違う学生が {g.nameMismatchCount}人います。
            {value.allowNameMismatch ? 'この授業も取り込みます。' : 'この授業は取り込みません。'}
          </strong>
          <ul style={{ margin: '0.3rem 0 0.3rem 1.1rem', padding: 0 }}>
            {g.nameMismatches.slice(0, 10).map(x => (
              <li key={x.studentId}>{x.studentId}：名簿「{x.fileName}」／登録「{x.knownName || '—'}」</li>
            ))}
            {g.nameMismatchCount > 10 && <li>ほか {g.nameMismatchCount - 10}人</li>}
          </ul>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <input type="checkbox" checked={!!value.allowNameMismatch}
                   onChange={e => onChange({ allowNameMismatch: e.target.checked })} />
            学籍番号を確かめたので、この授業も取り込む（名簿の名前で登録を上書きします）
          </label>
        </div>
      )}

      {/* 既存の授業を選んだとき、その授業に足されるもの【2026-10-05】 */}
      {/^\d+$/.test(String(value.to || '')) && (
        diff && String(diff.classId) === String(value.to) ? (() => {
          const parts = [];
          if (diff.addStudents > 0) parts.push(`履修者 ${diff.addStudents}人を追加`);
          if (diff.addSchedules.length > 0) parts.push(`曜日時限 ${diff.addSchedules.join('・')} を追加`);
          if (diff.removeCancelled > 0) parts.push(`履修取消の ${diff.removeCancelled}人を外す`);
          if (diff.fill.length > 0) parts.push(`${diff.fill.join('・')}を記入`);
          return (
            <div style={{ fontSize: '0.8rem', marginTop: '0.35rem', padding: '0.4rem 0.6rem', borderRadius: 6,
                          background: parts.length ? '#eff6ff' : '#f0fdf4', color: '#1f2937' }}>
              {parts.length
                ? <>この授業に足されるもの：<strong>{parts.join('、')}</strong></>
                : <>足されるものはありません（この名簿の分はもう入っています）</>}
              <div style={{ color: '#6b7280', marginTop: '0.15rem' }}>
                もう履修している {diff.alreadyEnrolled}人はそのまま
                {diff.notInFile > 0 && ` ・ この名簿に居ない履修者 ${diff.notInFile}人（manaba などで入れた人）も外しません`}
                {diff.existingSchedules > 0 && ` ・ 曜日時限はもう ${diff.existingSchedules}件あります`}
              </div>
            </div>
          );
        })() : (
          <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.35rem' }}>この授業に足されるものを確かめています…</div>
        )
      )}

      {/* 候補があるときは注意を出す */}
      {g.candidates.length > 0 && value.to === 'new' && (
        <p style={{ fontSize: '0.8rem', color: '#b45309', marginTop: '0.35rem' }}>
          ⚠ 似た授業が既にあります。新しく作ると二重になります。
        </p>
      )}

      {/* 取消の学生 */}
      {g.cancelled.length > 0 && (
        <details style={{ marginTop: '0.4rem' }} open={open}
                 onToggle={e => setOpen(e.target.open)}>
          <summary style={{ fontSize: '0.8rem', color: '#2563eb', cursor: 'pointer' }}>
            履修取消 {g.cancelled.length}人を見る
          </summary>
          <div className="table-wrap" style={{ marginTop: '0.4rem' }}>
            <table className="data-table">
              <thead><tr><th>学籍番号</th><th>氏名</th><th>理由</th><th>取消日</th></tr></thead>
              <tbody>
                {g.cancelled.map((s, i) => (
                  <tr key={i}>
                    <td>{s.studentId}</td><td>{s.name}</td>
                    <td>{s.reason || '—'}</td><td>{s.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ── 取り込んだあとの結果 ────────────────────────────────────────
function ImportResult({ result, onReset, embedded }) {
  return (
    <div style={{ marginTop: '0.9rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.5rem' }}>
        <Stat label="作った授業"   value={result.classesCreated} />
        <Stat label="既存に入れた" value={result.classesMatched} />
        <Stat label="履修登録"     value={result.enrolled} />
        <Stat label="作った学生"   value={result.usersCreated} />
        <Stat label="曜日時限"     value={result.schedulesCreated} />
        <Stat label="取消で除外"   value={result.cancelledSkipped} />
        {result.cancelledRemoved > 0 && <Stat label="取消で名簿から外した" value={result.cancelledRemoved} />}
      </div>

      {(result.cancelledRemovedList || []).length > 0 && (
        <details style={{ marginTop: '0.6rem', fontSize: '0.85rem' }}>
          <summary>履修取消で名簿から外した {result.cancelledRemovedList.length}人を見る</summary>
          <ul style={{ margin: '0.3rem 0 0 1.2rem' }}>
            {result.cancelledRemovedList.map((x, i) => (
              <li key={i}>{typeof x === 'string' ? x : [x.className, x.studentId, x.name].filter(Boolean).join(' ')}</li>
            ))}
          </ul>
        </details>
      )}

      {(result.warnings || []).length > 0 && (
        <div style={{ marginTop: '0.8rem' }}>
          {result.warnings.map((w, i) => (
            <p key={i} style={{ color: '#b45309', fontSize: '0.85rem', margin: '0.2rem 0' }}>⚠ {w}</p>
          ))}
        </div>
      )}

      {(result.linked || []).length > 0 && (
        <p style={{ fontSize: '0.85rem', color: '#166534', marginTop: '0.5rem' }}>
          {result.linked.length}件を親の授業にまとめました。
        </p>
      )}

      <div className="table-wrap" style={{ marginTop: '0.8rem' }}>
        <table className="data-table">
          <thead><tr><th>授業</th><th>番号</th><th>学期</th><th>決まり方</th><th>履修</th></tr></thead>
          <tbody>
            {result.classes.map((c, i) => (
              <tr key={i}>
                <td><strong>{c.name}</strong></td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{c.externalCode || '—'}</td>
                <td>{c.termName || '—'}</td>
                <td>{{ created: '新規作成', matched: '番号で一致', mapped: '指定' }[c.how] || c.how}</td>
                <td>{c.enrolled}人</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.6rem' }}>
        取り消したいときは「名簿取り込み履歴」から、この取り込み1回ぶんを戻せます。
      </p>
      <button className="btn-secondary" onClick={onReset} style={{ marginTop: '0.5rem' }}>
        {embedded ? '閉じる' : '続けて別のファイルを取り込む'}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e7ecf3', borderRadius: 10, padding: '0.6rem 0.8rem' }}>
      <div style={{ fontSize: '0.76rem', color: '#8a93a3' }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
        {value || 0}
      </div>
    </div>
  );
}
